import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { driverApi, DriverUser, IssueEsimRequest } from '../../services/driverApi';

interface AddEsimModalProps {
  user: DriverUser;
  onClose: () => void;
  onSuccess: () => void;
}

interface ESimProduct {
  item_code: string;
  item_name: string;
  gross_price_eur: number;
  plan_title: string;
  region_code: string;
  validity_days: number;
  data_mb: number;
  unlimited: boolean;
  provider_price_usd: number;
  product_type: string;
}

export const AddEsimModal: React.FC<AddEsimModalProps> = ({ user, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<IssueEsimRequest>({
    user_name: user.name,
    item_code: ''
  });
  const [loading, setLoading] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState<ESimProduct[]>([]);
  const [allProducts, setAllProducts] = useState<ESimProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setProductsLoading(true);
        // Load filtered products (region=CH, validity_days=30, data_mb=3072)
        const filteredResponse = await driverApi.getProducts('CH', 30, 3072);
        // Load all products for region CH
        const allResponse = await driverApi.getProducts('CH');
        
        if (filteredResponse.success) {
          setFilteredProducts(filteredResponse.products);
        }
        if (allResponse.success) {
          // Filter out products that are already in the filtered list to avoid duplicates
          const filteredItemCodes = new Set(
            filteredResponse.success ? filteredResponse.products.map(p => p.item_code) : []
          );
          const uniqueAllProducts = allResponse.products.filter(
            product => !filteredItemCodes.has(product.item_code)
          );
          setAllProducts(uniqueAllProducts);
        }
        
        // Set default selection to first filtered product if available, otherwise first from all products
        if (filteredResponse.success && filteredResponse.products.length > 0 && !formData.item_code) {
          setFormData(prev => ({ ...prev, item_code: filteredResponse.products[0].item_code }));
        } else if (allResponse.success && allResponse.products.length > 0 && !formData.item_code) {
          setFormData(prev => ({ ...prev, item_code: allResponse.products[0].item_code }));
        }
      } catch (error) {
        console.error('Failed to load products:', error);
        setErrors({ products: 'Failed to load products. Please try again.' });
      } finally {
        setProductsLoading(false);
      }
    };

    loadProducts();
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.item_code) {
      newErrors.item_code = 'Please select an eSIM product';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      await driverApi.issueEsim(formData);
      onSuccess();
    } catch (error) {
      console.error('Failed to issue eSIM:', error);
      setErrors({ submit: 'Failed to issue eSIM. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof IssueEsimRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-lg w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <h2 className="text-lg font-semibold">Add eSIM</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">User Information</h3>
            <div className="bg-gray-50 p-3 rounded-md">
              <div className="text-sm">
                <div className="font-medium">{user.first_name} {user.last_name}</div>
                <div className="text-gray-600">{user.email}</div>
                {user.phone && <div className="text-gray-600">{user.phone}</div>}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select eSIM Product *
              </label>
              {productsLoading ? (
                <div className="flex items-center justify-center p-4">
                  <div className="text-sm text-gray-500">Loading products...</div>
                </div>
              ) : filteredProducts.length === 0 && allProducts.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No products available
                </div>
              ) : (
                <div className="space-y-4">
                  {/* First list: Filtered products (30 days, 3072 MB) */}
                  {filteredProducts.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-2 uppercase">
                        30 Days • 3 GB
                      </div>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {filteredProducts.map((product) => (
                          <label
                            key={product.item_code}
                            className={`flex items-center p-3 border rounded-md cursor-pointer hover:bg-gray-50 ${
                              formData.item_code === product.item_code
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="item_code"
                              value={product.item_code}
                              checked={formData.item_code === product.item_code}
                              onChange={(e) => handleInputChange('item_code', e.target.value)}
                              className="mr-3"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{product.item_name}</div>
                              <div className="text-sm text-gray-600">
                                {product.plan_title} • {product.validity_days} days
                                {product.unlimited ? ' • Unlimited' : ` • ${product.data_mb} MB`}
                              </div>
                              <div className="text-xs text-gray-500">{product.item_code}</div>
                            </div>
                            <div className="text-sm font-medium text-gray-900">
                              €{product.gross_price_eur.toFixed(2)}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Second list: All products for CH region */}
                  {allProducts.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-2 uppercase">
                        All Switzerland Products
                      </div>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {allProducts.map((product) => (
                          <label
                            key={product.item_code}
                            className={`flex items-center p-3 border rounded-md cursor-pointer hover:bg-gray-50 ${
                              formData.item_code === product.item_code
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="item_code"
                              value={product.item_code}
                              checked={formData.item_code === product.item_code}
                              onChange={(e) => handleInputChange('item_code', e.target.value)}
                              className="mr-3"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{product.item_name}</div>
                              <div className="text-sm text-gray-600">
                                {product.plan_title} • {product.validity_days} days
                                {product.unlimited ? ' • Unlimited' : ` • ${product.data_mb} MB`}
                              </div>
                              <div className="text-xs text-gray-500">{product.item_code}</div>
                            </div>
                            <div className="text-sm font-medium text-gray-900">
                              €{product.gross_price_eur.toFixed(2)}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {errors.item_code && (
                <p className="text-sm text-red-600 mt-1">{errors.item_code}</p>
              )}
              {errors.products && (
                <p className="text-sm text-red-600 mt-1">{errors.products}</p>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Important</h3>
                  <div className="mt-1 text-sm text-blue-700">
                    <p>An email with the eSIM QR code will be sent to the user automatically after successful issuance.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

            {errors.submit && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}
          </form>
        </div>

        <div className="border-t p-6 flex-shrink-0">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || productsLoading || (filteredProducts.length === 0 && allProducts.length === 0)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Issuing...' : 'Issue eSIM'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
